import { useLayoutEffect, useRef } from "react";

/** A pairing code has 8 characters, shown as "ABCD-EFGH" (server pairing.ts). */
export const PAIRING_CODE_LENGTH = 8;
const HALF = PAIRING_CODE_LENGTH / 2;

/** Only the letters and digits of what was typed, in capitals: spaces and dashes don't count. */
function characters(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** "EG57QF92" → "EG57-QF92"; the dash appears as soon as the first four are typed. */
function withDash(chars: string): string {
  return chars.length < HALF ? chars : `${chars.slice(0, HALF)}-${chars.slice(HALF)}`;
}

/**
 * What the field shows after the customer typed, pasted or erased something, and where the cursor
 * goes: the dash comes by itself, so nobody has to look for it on the keyboard (Furkan, 09/10/2026).
 * Erasing the dash erases the character before it, otherwise the dash would come straight back and
 * backspace would seem stuck (the Delete key erases the character after it). `cursor` is where the
 * cursor stood in `typed` (default: at the end).
 */
export function formatPairingCode(
  typed: string,
  before = "",
  cursor = typed.length,
  forward = false,
): { code: string; caret: number } {
  let chars = characters(typed);
  let charsBefore = characters(typed.slice(0, cursor)).length;
  if (typed.length < before.length && chars === characters(before)) {
    if (forward) chars = chars.slice(0, charsBefore) + chars.slice(charsBefore + 1);
    else if (charsBefore > 0) {
      chars = chars.slice(0, charsBefore - 1) + chars.slice(charsBefore);
      charsBefore--;
    }
  }
  chars = chars.slice(0, PAIRING_CODE_LENGTH);
  const code = withDash(chars);
  return { code, caret: caretAt(code, Math.min(charsBefore, chars.length)) };
}

/** Where the cursor goes in the formatted code: right after the same number of letters and digits. */
function caretAt(code: string, charsBefore: number): number {
  if (charsBefore <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < code.length; index++) {
    if (code[index] !== "-") seen++;
    // after the fourth character the cursor jumps past the dash, ready for the fifth
    if (seen === charsBefore) return code[index + 1] === "-" ? index + 2 : index + 1;
  }
  return code.length;
}

interface Props {
  value: string;
  onChange: (code: string) => void;
  className?: string;
  autoFocus?: boolean;
}

/** The field for the one-time pairing code, on the till as well as in the Tafo app. */
export function PairingCodeInput({ value, onChange, className = "pairing-code", autoFocus }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  // the code is reformatted, so React would put the cursor at the end: put it back where the typing was
  useLayoutEffect(() => {
    const field = input.current;
    if (caret.current === null || field === null || document.activeElement !== field) return;
    field.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [value]);

  return (
    <input
      ref={input}
      className={className}
      value={value}
      onChange={(event) => {
        const typed = event.target.value;
        const forward = (event.nativeEvent as InputEvent).inputType === "deleteContentForward";
        const { code, caret: at } = formatPairingCode(
          typed,
          value,
          event.target.selectionStart ?? typed.length,
          forward,
        );
        caret.current = at;
        onChange(code);
      }}
      placeholder="ABCD-EFGH"
      autoComplete="off"
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      enterKeyHint="go"
      required
      autoFocus={autoFocus}
    />
  );
}
